import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription, Subject, debounceTime } from 'rxjs';
import { environment } from '../../../environment/environment';
import { ComponentInputBase } from '../../../models/ComponentInputBase.model';
import { ComponentService } from '../../../services/component/component.service';
import { DunaEventsFilterOverlayService } from '../../../services/duna-events-filter-overlay/duna-events-filter-overlay.service';
import { EventsFilterStateService } from '../../../services/events-filter/events-filter-state.service';
import {
  EventsFilterCriteria,
  FilterEducationalPath,
  FilterFacetCounts,
  FilterLocation,
  FilterMetadata,
  FilterModule,
  FilterOrganization,
  PathVersionOption,
  createEmptyCriteria,
  createEmptyFacetCounts,
  canShowGroupCalendarEventsFilter,
} from '../../../services/events-filter/events-filter.model';
import { EVENT_TYPE_FILTER_LABELS } from '../../../constants/event-types';
import {
  FilterMultiSelectComponent,
  FilterSelectOption,
} from './filter-multi-select.component';

export interface FilterSettings {
  filterKey?: string;
  title?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  showEventTypes?: boolean;
  showResetButton?: boolean;
  showResultCount?: boolean;
  showOrganizations?: boolean;
}

@Component({
  selector: 'app-duna-events-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    MatSliderModule,
    MatExpansionModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    FilterMultiSelectComponent,
  ],
  templateUrl: './duna-events-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-events-filter.component.scss',
})
export class DunaEventsFilterComponent
  extends ComponentInputBase
  implements OnInit, OnDestroy
{
  @Input() filterKey = 'events-filter';
  @Input() settings: FilterSettings | null = null;

  isLoading = true;
  isOverlayMode = false;
  isMobileOverlay = false;
  resultCount = 0;
  facetCounts: FilterFacetCounts = createEmptyFacetCounts();
  readonly eventTypeFilterLabels = EVENT_TYPE_FILTER_LABELS;

  metadata: FilterMetadata | null = null;
  criteria: EventsFilterCriteria = createEmptyCriteria();

  pathVersions: PathVersionOption[] = [];
  selectedPathVersionId: number | null = null;
  distanceSliderValue = 0;
  locationError = '';
  selectSearch = {
    org: '',
    path: '',
    module: '',
    location: '',
  };

  private subs: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private static primaryInitDone = false;

  constructor(
    componentService: ComponentService,
    private filterState: EventsFilterStateService,
    private overlayService: DunaEventsFilterOverlayService,
    private cdr: ChangeDetectorRef
  ) {
    super(componentService);
  }

  get effectiveSettings(): FilterSettings {
    return {
      title: 'Filter',
      searchPlaceholder: 'Søg begivenheder, stier, moduler…',
      showSearch: true,
      showEventTypes: true,
      showResetButton: true,
      showResultCount: true,
      showOrganizations: true,
      ...this.settings,
    };
  }

  get visibility() {
    return this.metadata?.visibility;
  }

  get showGroupCalendarEventsFilter(): boolean {
    return canShowGroupCalendarEventsFilter(this.metadata, this.criteria);
  }

  ngOnInit(): void {
    this.syncResponsiveMode();

    this.subs.push(
      this.filterState.initialize().subscribe(() => {
        this.metadata = this.filterState.metadata;
        this.criteria = { ...this.filterState.criteria };
        this.syncEventTypeFlags();
        this.isLoading = false;
        this.loadPathVersionsIfNeeded();
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.filterState.criteria$.subscribe((c) => {
        this.criteria = { ...c };
        this.syncEventTypeFlags();
        this.loadPathVersionsIfNeeded();
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.filterState.metadata$.subscribe((m) => {
        if (!m) {
          return;
        }
        this.metadata = m;
        this.syncEventTypeFlags();
        this.loadPathVersionsIfNeeded();
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.filterState.lifecycle$.subscribe((lifecycle) => {
        this.isLoading = lifecycle === 'loading-metadata';
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.filterState.facetCounts$.subscribe((counts) => {
        this.facetCounts = counts;
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.filterState.resultCount$.subscribe((n) => {
        this.resultCount = n;
        this.cdr.markForCheck();
      })
    );

    this.subs.push(
      this.searchSubject.pipe(debounceTime(400)).subscribe((text) => {
        this.applyChange({ searchText: text });
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.syncResponsiveMode();
  }

  private syncResponsiveMode(): void {
    const width = Math.round(window.visualViewport?.width || window.innerWidth);
    if (this.isOverlayMode) {
      this.isMobileOverlay = width <= environment.mobileWidth;
    } else {
      this.isOverlayMode = width <= environment.tabletWidth;
      this.isMobileOverlay = width <= environment.mobileWidth;
    }
    this.cdr.markForCheck();
  }

  // Event type chip helpers
  typeUddannelser = true;
  typeOvelser = false;
  typeKurser = true;
  typeAktiviteter = true;

  private syncEventTypeFlags(): void {
    const types = this.criteria.eventType || [];
    this.typeUddannelser = types.includes('education');
    this.typeOvelser = types.includes('exercise');
    this.typeKurser = types.includes('course');
    this.typeAktiviteter = types.includes('activity');
  }

  private buildEventTypes(): string[] {
    const types: string[] = [];
    if (this.typeUddannelser) types.push('education');
    if (this.typeOvelser && this.visibility?.showExercises)
      types.push('exercise');
    if (this.typeKurser) types.push('course');
    if (this.typeAktiviteter) types.push('activity');
    return types;
  }

  facetCount(map: Record<string, number>, key: string | number): number {
    return map[String(key)] ?? 0;
  }

  eventTypeCount(slug: string): number {
    return this.facetCount(this.facetCounts.eventTypes, slug);
  }

  hiddenCount(key: 'subEvents' | 'fullEvents' | 'participated'): number {
    const n = this.facetCounts.hiddenCounts?.[key] ?? 0;
    if (key === 'subEvents' && this.criteria.hideSubEvents) {
      return 0;
    }
    if (key === 'fullEvents' && this.criteria.hideFullEvents) {
      return 0;
    }
    if (key === 'participated' && this.criteria.hideParticipatedEvents) {
      return 0;
    }
    return n;
  }

  formatSelectedOrganizations(): string {
    const ids = this.criteria.organizationIds || [];
    if (!ids.length || !this.metadata) {
      return '';
    }
    return this.metadata.organizations
      .filter((o) => ids.includes(o.id))
      .map((o) => this.getOrgLabel(o))
      .join(', ');
  }

  formatSelectedPaths(): string {
    const ids = this.criteria.educationalPathIds || [];
    if (!ids.length || !this.metadata) {
      return '';
    }
    return this.metadata.educationalPaths
      .filter((p) => ids.includes(p.path_id))
      .map((p) => this.getPathLabel(p))
      .join(', ');
  }

  formatSelectedModules(): string {
    const ids = this.criteria.moduleIds || [];
    if (!ids.length || !this.metadata) {
      return '';
    }
    return this.metadata.modules
      .filter((m) => ids.includes(m.module_id))
      .map((m) => m.name)
      .join(', ');
  }

  formatSelectedLocations(): string {
    const guids = this.criteria.locationGuids || [];
    if (!guids.length || !this.metadata) {
      return '';
    }
    return this.metadata.locations
      .filter((l) => guids.includes(l.guid))
      .map((l) => l.locations_name)
      .join(', ');
  }

  get filteredOrganizations(): FilterOrganization[] {
    const orgs = this.metadata?.organizations ?? [];
    return orgs.filter((o) =>
      this.matchesSelectSearch(this.getOrgLabel(o), this.selectSearch.org)
    );
  }

  get sortedFilteredPaths(): FilterEducationalPath[] {
    const paths = this.getPathsForSelectedOrgs(this.criteria.organizationIds || []);
    return [...paths]
      .sort((a, b) =>
        this.getPathLabel(a).localeCompare(this.getPathLabel(b), 'da', {
          sensitivity: 'base',
        })
      )
      .filter((p) =>
        this.matchesSelectSearch(this.getPathLabel(p), this.selectSearch.path)
      );
  }

  get organizationSelectOptions(): FilterSelectOption<number>[] {
    return this.filteredOrganizations.map((org) => ({
      value: org.id,
      label: this.getOrgLabel(org),
      facetCount: this.facetCount(this.facetCounts.organizations, org.id),
    }));
  }

  get educationalPathSelectOptions(): FilterSelectOption<number>[] {
    return this.sortedFilteredPaths.map((path) => ({
      value: path.path_id,
      label: this.getPathLabel(path),
      facetCount: this.facetCount(
        this.facetCounts.educationalPaths,
        path.path_id
      ),
    }));
  }

  get moduleSelectOptions(): FilterSelectOption<number>[] {
    return this.sortedFilteredModules.map((mod) => ({
      value: mod.module_id,
      label: mod.name,
      facetCount: this.facetCount(this.facetCounts.modules, mod.module_id),
    }));
  }

  get locationSelectOptions(): FilterSelectOption<string>[] {
    return this.sortedFilteredLocations.map((loc) => ({
      value: loc.guid,
      label: loc.locations_name,
      facetCount: this.facetCount(this.facetCounts.locations, loc.guid),
    }));
  }

  get sortedFilteredModules(): FilterModule[] {
    const mods = [...(this.metadata?.modules ?? [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'da', { sensitivity: 'base' })
    );
    return mods.filter((m) =>
      this.matchesSelectSearch(m.name, this.selectSearch.module)
    );
  }

  get sortedFilteredLocations(): FilterLocation[] {
    const locs = [...(this.metadata?.locations ?? [])].sort((a, b) =>
      (a.locations_name || '').localeCompare(b.locations_name || '', 'da', {
        sensitivity: 'base',
      })
    );
    return locs.filter((l) =>
      this.matchesSelectSearch(l.locations_name, this.selectSearch.location)
    );
  }

  onSelectSearchChange(
    key: keyof DunaEventsFilterComponent['selectSearch'],
    value: string
  ): void {
    this.selectSearch[key] = value;
    this.cdr.markForCheck();
  }

  getPathVersionLabel(version: PathVersionOption): string {
    return (
      version.display_name || version.name || 'Version ' + version.version_id
    );
  }

  private getPathsForSelectedOrgs(
    orgIds: number[]
  ): FilterEducationalPath[] {
    const paths = this.metadata?.educationalPaths ?? [];
    if (!orgIds.length) {
      return paths;
    }
    return paths.filter((p) =>
      (p.organizationIds ?? []).some((oid) => orgIds.includes(oid))
    );
  }

  private matchesSelectSearch(label: string, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) {
      return true;
    }
    return (label || '').toLowerCase().includes(q);
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  onEventTypesChange(): void {
    this.applyChange({ eventType: this.buildEventTypes() });
  }

  onToggleChange(): void {
    this.applyChange({
      hideSubEvents: this.criteria.hideSubEvents,
      hideFullEvents: this.criteria.hideFullEvents,
      hideParticipatedEvents: this.criteria.hideParticipatedEvents,
      showGroupCalendarEvents: this.criteria.showGroupCalendarEvents,
    });
  }

  onOrganizationIdsChange(organizationIds: number[]): void {
    const validPathIds = this.getPathsForSelectedOrgs(organizationIds).map(
      (p) => p.path_id
    );
    const currentPathIds = this.criteria.educationalPathIds || [];
    const prunedPathIds = currentPathIds.filter((id) =>
      validPathIds.includes(id)
    );

    const validModuleIds = (this.metadata?.modules ?? []).map((m) => m.module_id);
    const currentModuleIds = this.criteria.moduleIds || [];
    const prunedModuleIds = currentModuleIds.filter((id) =>
      validModuleIds.includes(id)
    );

    const changes: Partial<EventsFilterCriteria> = {
      organizationIds: [...organizationIds],
    };
    if (prunedPathIds.length !== currentPathIds.length) {
      changes.educationalPathIds = prunedPathIds;
      changes.educationalPathVersionIds = [];
      this.selectedPathVersionId = null;
      this.pathVersions = [];
    }
    if (prunedModuleIds.length !== currentModuleIds.length) {
      changes.moduleIds = prunedModuleIds;
    }

    if (
      !canShowGroupCalendarEventsFilter(this.metadata, {
        ...this.criteria,
        organizationIds: [...organizationIds],
      })
    ) {
      changes.showGroupCalendarEvents = false;
    }

    this.applyChange(changes);
  }

  onPathsChange(selectedIds: number[] | null | undefined): void {
    const educationalPathIds = [...(selectedIds || [])];
    // Keep local model in sync before criteria$ echoes back (avoids stale reads).
    this.criteria = {
      ...this.criteria,
      educationalPathIds,
      educationalPathVersionIds: [],
    };
    this.selectedPathVersionId = null;
    this.pathVersions = [];
    this.applyChange({
      educationalPathIds,
      educationalPathVersionIds: [],
    });
    this.loadPathVersionsIfNeeded();
  }

  onModuleIdsChange(moduleIds: number[]): void {
    this.applyChange({ moduleIds: [...moduleIds] });
  }

  onLocationGuidsChange(locationGuids: string[]): void {
    this.applyChange({ locationGuids: [...locationGuids] });
  }

  onPathVersionChange(versionId: number | null): void {
    this.selectedPathVersionId = versionId;
    this.applyChange({
      educationalPathVersionIds: versionId ? [versionId] : [],
    });
  }

  useMyLocation(): void {
    if (!navigator.geolocation) {
      this.locationError = 'Geolocation understøttes ikke i denne browser.';
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locationError = '';
        this.applyChange({
          distanceFilter: {
            enabled: true,
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            radiusKm: this.criteria.distanceFilter?.radiusKm ?? null,
          },
        });
        this.cdr.markForCheck();
      },
      () => {
        this.locationError = 'Kunne ikke hente din placering.';
        this.cdr.markForCheck();
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  onDistanceSliderChange(value: number): void {
    this.distanceSliderValue = value;
    const df = this.criteria.distanceFilter;
    if (!df?.latitude || !df?.longitude) return;
    this.applyChange({
      distanceFilter: {
        ...df,
        enabled: true,
        radiusKm: value <= 0 ? null : value,
      },
    });
  }

  onDistanceInputChange(value: string): void {
    const num = parseFloat(value);
    const df = this.criteria.distanceFilter;
    if (!df?.latitude || !df?.longitude) return;
    this.distanceSliderValue = isNaN(num) ? 0 : num;
    this.applyChange({
      distanceFilter: {
        ...df,
        enabled: true,
        radiusKm: isNaN(num) || num <= 0 ? null : num,
      },
    });
  }

  clearDistance(): void {
    this.distanceSliderValue = 0;
    this.applyChange({
      distanceFilter: {
        enabled: false,
        latitude: null,
        longitude: null,
        radiusKm: null,
      },
    });
  }

  clearFilters(): void {
    this.filterState.resetToDefaults();
    this.selectSearch = { org: '', path: '', module: '', location: '' };
    this.selectedPathVersionId = null;
    this.pathVersions = [];
    this.distanceSliderValue = 0;
    this.cdr.markForCheck();
  }

  onClearFiltersClick(event: Event): void {
    event.stopPropagation();
    this.clearFilters();
  }

  closeOverlay(): void {
    this.overlayService.close();
  }

  getOrgLabel(org: FilterOrganization): string {
    return org.full_name || org.org_name;
  }

  getPathLabel(path: FilterEducationalPath): string {
    return path.full_name || path.name;
  }

  showPathVersionDropdown(): boolean {
    return (
      (this.criteria.educationalPathIds?.length ?? 0) === 1 &&
      this.pathVersions.length > 1
    );
  }

  private applyChange(partial: Partial<EventsFilterCriteria>): void {
    this.filterState.updateCriteria(partial);
  }

  private loadPathVersionsIfNeeded(): void {
    const ids = this.criteria.educationalPathIds || [];
    if (ids.length !== 1 || !this.metadata) {
      this.pathVersions = [];
      return;
    }
    const path = this.metadata.educationalPaths.find(
      (p) => p.path_id === ids[0]
    );
    const versions = path?.versions ?? [];
    // Prefer metadata (available to all calendar users). Avoid /paths/:uuid/versions —
    // that endpoint is role-gated and returns 401 for ordinary users, which triggers token refresh.
    if (!path || versions.length <= 1) {
      this.pathVersions = [];
      return;
    }
    this.pathVersions = versions;
  }
}
